import { EventEmitter, Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { lastValueFrom, Observable } from "rxjs";
import { ValidationRequest } from '../../Model/validation/ValidationRequest';
import { ValidationRequestCatalog } from '../../Model/validation/ValidationRequestCatalog';
import { FieldNames, fieldNames } from '../../Model/FieldNames';
import { ModelService } from './../model.service';
import { TranslateService } from '@ngx-translate/core';
import { Product } from '../../Model/Catalog/Product';
import { ValidationResult } from '../../Model/validation/ValidationResult';
import { Catalog } from '../../Model/Catalog/Catalog';
import { ValidationMethod } from "../../Model/validation/ValidationMethod";
import { StringResponse } from "../../Model/Dto/StringResponse";
import { ValidationField } from "../../Model/validation/ValidationField";
import { InitializedEvent } from 'devextreme/ui/validator';
import { LoginService } from "../login.service";


@Injectable()
export class ValidationService {
  public validationResultChanged: EventEmitter<string> = new EventEmitter<string>();

  public fieldNames: FieldNames;
  public validationResult: ValidationResult;
  public validateFieldRecord: Record<string, (params) => any> = {};

  constructor(private http: HttpClient, public modelService: ModelService,
    public translate: TranslateService, private loginService: LoginService) {
    this.validationResult = new ValidationResult();
    this.validationResult.validateNumbers = new Array<number>();
    this.validationResult.validateNumbers.push(0);
    this.validationResult.validateNumbers.push(0);
    this.validationResult.validateNumbers.push(0);
    this.validationResult.validateNumbers.push(0);
    this.validationResult.validateNumbers.push(0);
    this.fieldNames = fieldNames;
    this.validate = this.validate.bind(this);
    this.validateField = this.validateField.bind(this);
  }

  getValidationMethod(id: string): Observable<ValidationMethod> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("methodId", id)
    }
    return (this.http.get<ValidationMethod>("api/validation/GetValidationMethod", options)) as any;
  }

  getValidationMethodsForEditor(): Observable<ValidationMethod[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams()
    }
    return (this.http.get<ValidationMethod[]>("api/validation/GetValidationMethodsForEditor", options)) as any;
  }

  getValidationMethodsForCatalog(): Observable<ValidationMethod[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams()
    }
    return (this.http.get<ValidationMethod[]>("api/validation/GetValidationMethodsForCatalog", options)) as any;
  }

  getSharedValidationMethodsCreatedByCustomer(): Promise<ValidationMethod[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams()
    }
    return lastValueFrom((this.http.get<ValidationMethod[]>("api/validation/GetSharedValidationMethodsCreatedByCustomer", options)) as Observable<ValidationMethod[]>);
  }

  deleteValidationMethod(model: ValidationMethod): Observable<StringResponse> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      body: model
    }
    return this.http.delete<StringResponse>("api/validation/DeleteValidationMethod", options);
  }

  createValidationMethod(model: ValidationMethod): Observable<ValidationMethod> {
    return this.http.post<ValidationMethod>("api/validation/CreateValidationMethod", model);
  }

  updateValidationMethod(model: ValidationMethod): Observable<StringResponse> {
    return this.http.post<StringResponse>("api/validation/UpdateValidationMethod", model);
  }

  validationMethodChanged(model: ValidationMethod): Observable<boolean> {
    return this.http.post<boolean>("api/validation/ValidationMethodChanged", model);
  }

  getDataQualityAreas(): Observable<string[]> {
    return this.http.get<string[]>("api/validation/GetDataQualityAreas");
  }

  getValidationFieldGroups(): Observable<string[]> {
    return this.http.get<string[]>("api/validation/GetValidationFieldGroups");
  }

  getValidationFields(): Observable<ValidationField[]> {
    return this.http.get<ValidationField[]>("api/validation/GetValidationFields");
  }

  getValidationTargetOptions(): Promise<string[]> {
    return lastValueFrom(this.http.get<string[]>("api/validation/GetValidationTargetOptions"));
  }

  createValidationFieldIdentifier(fieldName: string, systemKey = "-1", elementKey = "-1") {
    return fieldName + "|" + systemKey + "|" + elementKey;
  }

  validateNumberField(fieldName: string, systemKey = "-1", elementKey = "-1"){
    if
    (this.loginService.isReadonly) {
      return;
    }
    var validationFieldIdentifier = this.createValidationFieldIdentifier(fieldName, systemKey, elementKey);
    if (this.validateFieldRecord[validationFieldIdentifier]) {
      return this.validateFieldRecord[validationFieldIdentifier];
    }
    return this.validateFieldRecord[validationFieldIdentifier] = (params) => {
      var splittedIdentifier = validationFieldIdentifier.split("|");
      if (splittedIdentifier[0] == this.fieldNames.fName && params.value == "temp id") {
        return Promise.resolve(true);
      }
      if (typeof params.value === "string") {
        params.value = params.value.replace(',', '.');
        params.rule.value = params.rule.value.replace(',', '.');
      }
      return this.validate(params, splittedIdentifier[0], splittedIdentifier[1], splittedIdentifier[2]);
    };
  }

  validateField(fieldName: string, systemKey = "-1", elementKey = "-1") {

    if (this.loginService.isReadonly) {
      return;
    }
    var validationFieldIdentifier = this.createValidationFieldIdentifier(fieldName, systemKey, elementKey);
    if (this.validateFieldRecord[validationFieldIdentifier]) {
      return this.validateFieldRecord[validationFieldIdentifier];
    }
    return this.validateFieldRecord[validationFieldIdentifier] = (params) => {
      var splittedIdentifier = validationFieldIdentifier.split("|");
      if (splittedIdentifier[0] == this.fieldNames.fName && params.value == "temp id") {
        return Promise.resolve(true);
      }
      return this.validate(params, splittedIdentifier[0], splittedIdentifier[1], splittedIdentifier[2]);
    };
  }

  async validate(params, field: string, systemKey = "-1", elementKey = "-1") {
    if (!this.modelService.catalogService.catalog || this.loginService.isReadonly)
      return true;

    let validationRequest = new ValidationRequest();
    validationRequest.methodId = this.modelService.catalogService.catalog.validationMethodId;
    validationRequest.product = this.modelService.catalogService.product;
    validationRequest.field = field;
    validationRequest.systemKey = systemKey;
    validationRequest.elementKey = elementKey;
    validationRequest.isBooleanRadioGroup = false;

    let response: any;
    try {
      response = await lastValueFrom(this.http.post("api/validation/validate", validationRequest));
    }
    catch (error){
      if(error.status == 403){
        setTimeout(() => {
          const $element = params.validator.$element();
          $element.removeClass("dx-show-invalid-badge");
          $element.removeClass("dx-valid");
          $element.removeClass("dx-invalid");
        }, 100);
        return true;
      }
      throw error;
    }
 
    let isValid = response["isValid"];
    let message = response["message"];
    let noValidation = response["noValidation"];;
    if (message && message != "") {
      message = this.translate.instant(message);
    }
    if (this.modelService.catalogService.product) {
      this.modelService.catalogService.product.prepareView(this.modelService.catalogService.catalog?.alternativeFeatureSystemIDUsedForExport);
    }
    params.rule.message = message;
    params.rule.isValid = isValid;

    setTimeout(() => {
      const $element = params.validator.$element();

      if (noValidation) {
        $element.removeClass("dx-show-invalid-badge");
        $element.removeClass("dx-valid");
        $element.removeClass("dx-invalid");
      }
    }, 100);

    return isValid;
  }

  async validateFieldDirect(field: string, systemKey = "-1", elementKey = "-1"): Promise<{ isValid: boolean, message: string, noValidation: boolean }> {
    if (!this.modelService.catalogService.catalog || !this.modelService.catalogService.product || this.loginService.isReadonly) {
      return { isValid: true, message: "", noValidation: true };
    }

    let validationRequest = new ValidationRequest();
    validationRequest.methodId = this.modelService.catalogService.catalog.validationMethodId;
    validationRequest.product = this.modelService.catalogService.product;
    validationRequest.field = field;
    validationRequest.systemKey = systemKey;
    validationRequest.elementKey = elementKey;
    validationRequest.isBooleanRadioGroup = false;

    let response: any = await lastValueFrom(this.http.post("api/validation/validate", validationRequest));
    let message = response["message"];
    if (message && message != "") {
      message = this.translate.instant(message);
    }
    return { isValid: response["isValid"], message: message, noValidation: response["noValidation"] };
  }

  async validateBooleanRadioGroup(params, field: string) {
    if (!this.modelService.catalogService.catalog || this.loginService.isReadonly)
      return true;

    let validationRequest = new ValidationRequest();
    validationRequest.methodId = this.modelService.catalogService.catalog.validationMethodId;
    validationRequest.product = this.modelService.catalogService.product;
    validationRequest.field = field;
    validationRequest.isBooleanRadioGroup = true;
    validationRequest.booleanRadioGroupValue = params.value;

    var response = await lastValueFrom(this.http.post("api/validation/validate", validationRequest));
    params.rule.isValid = response["isValid"];
    params.rule.message = response["message"];
    if (params.rule.message && params.rule.message != "") {
      params.rule.message = this.translate.instant(params.rule.message);
    }
    if (this.modelService.catalogService.product) {
      this.modelService.catalogService.product.prepareView(this.modelService.catalogService.catalog?.alternativeFeatureSystemIDUsedForExport);
    }
    return params.rule.isValid;
  }

  async validateCatalogId(params: any, validationRequest: ValidationRequestCatalog) {
    var response = await lastValueFrom(this.http.post("api/validation/validateCatalog", validationRequest));

    params.rule.isValid = response["isValid"];
    params.rule.message = response["message"];
    if (params.rule.message && params.rule.message != "") {
      params.rule.message = this.translate.instant(params.rule.message);
    }

    return params.rule.isValid;
  }

  async directValidateCatalogId(validationRequest: ValidationRequestCatalog) {
    var response = await lastValueFrom(this.http.post("api/validation/validateCatalog", validationRequest));
    return response["isValid"];
  }

  validateProduct(product: Product) {
    if (!this.loginService.isReadonly) {
      this.validateProductPromise(product).then(() => {
      });
    }
  }

  validateProductPromise(product: Product) {
    if (this.loginService.isReadonly){
      return;
    }
    let that = this;
    return new Promise(function (resolve, reject) {
      if (!that.modelService.catalogService.catalog)
        that.modelService.catalogService.catalog = new Catalog();

      let validationRequest = new ValidationRequest();
      validationRequest.methodId = that.modelService.catalogService.catalog.validationMethodId;
      validationRequest.product = product;
      that.http.post("api/validation/validateProduct", validationRequest).subscribe((response: ValidationResult) => {
        that.validationResult = response;
        that.validationResult.validateNumbers = new Array<number>();
        that.validationResult.validateNumbers.push(response.validateNumbers0);
        that.validationResult.validateNumbers.push(response.validateNumbers1);
        that.validationResult.validateNumbers.push(response.validateNumbers2);
        that.validationResult.validateNumbers.push(response.validateNumbers3);
        that.validationResult.validateNumbers.push(response.validateNumbers4);
        resolve(true);
      },
        error => { reject(error); }
      );
    });
  }

  startAutomaticValidation(event: InitializedEvent) {
    if (this.loginService.currentCustomer.automaticValidation) {
      event.component.validate();
    }
  }
  
  private readonly validationSummaryGroups: { [group: string]: { isValid: () => boolean; messages: () => string[] } } = {
    media: { isValid: () => this.validationResult?.productMimesValid, messages: () => this.validationResult?.mediaMessages ?? [] },
    price: { isValid: () => this.validationResult?.productPricesValid, messages: () => this.validationResult?.priceMessages ?? [] },
    feature: { isValid: () => this.validationResult?.productFeaturesValid, messages: () => this.validationResult?.featureMessages ?? [] },
    legal: { isValid: () => this.validationResult?.productLegalValid, messages: () => this.validationResult?.legalMessages ?? [] }
  };

  showValidationSummary(group: string): boolean {
    const config = this.validationSummaryGroups[group];
    if (!config) {
      return false;
    }
    return this.loginService.currentCustomer.automaticValidation && !config.isValid() && config.messages().length > 0;
  }
  
  consolidatedValidationMessages(group: string): { message: string; count: number }[] {
    const config = this.validationSummaryGroups[group];
    if (!config) {
      return [];
    }
    const grouped = new Map<string, number>();
    for (const msg of config.messages()) {
      grouped.set(msg, (grouped.get(msg) ?? 0) + 1);
    }
    return Array.from(grouped, ([message, count]) => ({ message: this.translate.instant(message), count }));
  }
}
